
import { Component, Vue, Watch } from "vue-property-decorator"
import { namespace, Getter, State } from "vuex-class"

import CaseList from "@/components/New/Cases/Overview/CaseList.vue"
import ExpandableSidebar from "@/components/New/Page/ExpandableSidebar.vue"

import { ViewsService } from "@/services/viewsService"

import { User } from "@/types"
import { ViewFullSummary, ViewSummary } from "@/types/views/viewSummary"
import ViewFolders from "@/components/New/Folders/ViewFolders.vue"
import { Folder, FolderType, FolderItem } from "@/types/folder"
import api from "@/services/api"
import DataCache from "@/caching/caching"
import { CasesTable } from "@/types/case"
import { CurrentPageDetails, GetCasesForViewRequest } from "@/types/paginatedRequest"
import PageWrapper from "../Page/PageWrapper.vue"

const caseStore = namespace("cases")
const setupStore = namespace("setup")

@Component({
	components: {
		"page": PageWrapper,
		"case-list": CaseList,
		"sidebar": ExpandableSidebar,
		"view-folders": ViewFolders
	}
})
export default class Overview extends Vue {
	@State("userCache") private readonly userCache!: DataCache<User>;
	@Getter("getMe") private readonly me!: User;
	@Getter("getRecordName") private readonly recordName!: string;
	@Getter("getPluralizedRecordName") private readonly pluralRecordName!: string;

	@caseStore.State private viewsService!: ViewsService;
	@caseStore.Action private casesLoad!: () => void;
	@caseStore.Action private caseClear!: () => void;
	@caseStore.Action("detailsLoad") private detailsLoad!: () => void;
	@caseStore.Mutation("setActiveViewId") private setActiveViewId!: (viewId: string | null) => void;

	@setupStore.Action private loadFieldsIfUnloaded!: () => Promise<void>;

	private viewsSearchFilter: string = "";
	private caseSearchFilter: string = "";
	private folders: Folder[] = []

	private isMounted: boolean = false
	private loadingPage: boolean = false;
	private cacheId: string = "";

	/**
	 * @summary Mounted lifecycle hook.
	 */
	private async mounted (): Promise<void> {
		this.caseClear()
		this.detailsLoad()
		const loadUsersPromise = this.userCache.fetchIfNeeded()
		const loadFieldsPromise = this.loadFieldsIfUnloaded()
		const loadFoldersPromise = this.loadFolders()
		const loadViewsPromise: Promise<void> = this.viewsService.loadViews()

		await Promise.allSettled([loadUsersPromise, loadFieldsPromise, loadFoldersPromise, loadViewsPromise])
		this.isMounted = true
	}

	/**
	 * @summary Gets the summary for the currently selected view.
	 */
	private get viewSummary (): ViewFullSummary | null {
		return this.viewsService.viewSummary
	}

	private get casesTables (): CasesTable[] {
		return this.viewSummary?.casesTables ?? []
	}

	/**
	 * @summary filters the views based on the provided filter term and returns the result as a FolderItem[]
	 */
	private get filteredViews (): FolderItem[] | undefined {
		const lowerFilter = this.viewsSearchFilter ? this.viewsSearchFilter.toLowerCase() : ""
		const filtered = this.viewsSearchFilter
			? this.viewSummary?.summary?.filter(i => i.title.toLowerCase().includes(lowerFilter))
			: this.viewSummary?.summary

		return filtered?.map((fv: ViewSummary) => {
			return {
				...fv,
				itemCount: fv.caseCount
			}
		}) as FolderItem[]
	}

	/**
	 * @summary Gets the number of cases in the current view.
	 */
	private get totalRecordsAvailable (): number {
		return this.viewSummary?.casesTotal ?? 0
	}

	private totalRecordsShown (): number {
		return this.casesTables?.reduce((acc, current) => {
			return acc + current.totalRecords
		}, 0) ?? 0
	}

	/**
	 * @summary Gets the title for the currently selected view.
	 */
	private get viewTitle (): string {
		return this.viewSummary?.activeView?.title ?? "Records"
	}

	/**
	 * @summary Gets the text to use when displaying the number of records help text.
	 */
	private get caseTotalText (): string {
		const casesTotal = this.totalRecordsAvailable
		const casesShown = this.totalRecordsShown()
		const record: string = "record"
		const records: string = "records"

		if (this.casesTables.length > 0) {
			if (this.caseSearchFilter && /\S/.test(this.caseSearchFilter)) {
				const recordString = casesTotal === 1 ? record : records
				return `Showing ${casesShown} of ${casesTotal} ${recordString}`
			}

			const recordString = casesShown === 1 ? record : records
			return `${casesShown} ${recordString}`
		}

		return ""
	}

	// Retrieve all folders
	private async loadFolders (): Promise<void> {
		this.folders = await api.foldersGet(FolderType.View)
	}

	/**
	 * @summary Watcher fired when the selected view is changed via the route.
	 */
	@Watch("$route.params.viewId")
	private async onViewChanged (newViewId: string | null): Promise<void> {
		if (!newViewId) {
			return
		}

		this.caseSearchFilter = ""
		this.viewsService.activeViewId = newViewId

		await this.setCases()
	}

	private async setCases (searchTerm: string = ""): Promise<void> {
		await this.viewsService.loadViews(searchTerm, this.cacheId)
		this.cacheId = this.viewsService.viewSummary?.id ?? ""
	}

	private async loadNextPage (details: CurrentPageDetails): Promise<void> {
		if (this.viewsService.activeViewId) {
			try {
				this.loadingPage = true
				const request: GetCasesForViewRequest = {
					viewId: this.viewsService.activeViewId,
					page: details.pageNumber ?? 1,
					pageSize: details.pageSize ?? 20,
					searchTerm: details.searchTerm,
					timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					sortBy: details.sortBy,
					sortDesc: details.sortDesc,
					sortDataType: details.sortDataType,
					tableId: details.tableId,
					id: this.cacheId
				}

				const tableId = details.tableId ? details.tableId : details.value
				const cases = await api.casesForView(request)
				const tableToUpdate = this.casesTables.firstOrDefault((c: CasesTable) => c.value === tableId)

				if (tableToUpdate) {
					tableToUpdate.totalRecords = cases.totalItems
					tableToUpdate.cases = [...cases.data]
				}
			} catch (err) {
				console.error(err)
				await this.setCases()
			} finally {
				this.loadingPage = false
			}
		}
	}

	private async reloadInsightBySearch (searchTerm: string): Promise<void> {
		if (this.viewsService.activeViewId) {
			try {
				this.loadingPage = true
				await this.setCases(searchTerm)
			} catch (err) {
				console.error(err)
				this.setCases()
			} finally {
				this.loadingPage = false
			}
		}
	}
}
